/**
 * Cart Yardage Display Updater
 * Custom +/- buttons that update yardage display and sync with FSA
 */

(function() {
  'use strict';

  const CONFIG = {
    yardsToUnitsMultiplier: 2, // 1 unit = 0.5 yards, so 2 units = 1 yard
  };

  // Track pending requests to prevent race conditions
  const pendingRequests = new Map();

  /**
   * Update yardage display based on quantity
   */
  function updateYardageDisplay(cartItem, quantity) {
    const yardageDisplay = cartItem.querySelector('.cart-yardage-display');
    if (!yardageDisplay) return;

    // Convert units to yards
    const yards = quantity / CONFIG.yardsToUnitsMultiplier;

    // Format display text
    const yardText = yards === 1 ? 'yard' : 'yards';
    yardageDisplay.textContent = `${yards} ${yardText}`;

    // Store quantity in data attribute for reliable state management
    yardageDisplay.setAttribute('data-quantity', quantity);
  }

  /**
   * Get current quantity from DOM (single source of truth)
   */
  function getCurrentQuantity(cartItem) {
    // Cart page uses FSA hidden quantity input
    const cartInput = cartItem.querySelector('[data-fsa-hidden-quantity-cart]');
    if (!cartInput) {
      console.error('[Cart Yardage] Cart input not found');
      return 0;
    }
    const quantity = parseInt(cartInput.value, 10);
    console.log('[Cart Yardage] getCurrentQuantity returning:', quantity);
    return quantity || 0;
  }

  /**
   * Update cart quantity input to keep form submission in sync
   */
  function updateCartInput(cartItem, newQuantity) {
    // Update FSA hidden quantity input (this is what Shopify reads on checkout)
    const cartInput = cartItem.querySelector('[data-fsa-hidden-quantity-cart]');
    if (cartInput) {
      cartInput.value = newQuantity;
      console.log('[Cart Yardage] Updated cart input to:', newQuantity);
    }
  }

  /**
   * Show error message to user
   */
  function showError(cartItem, message) {
    // Remove any existing error messages
    const existingError = cartItem.querySelector('.cart-yardage-error');
    if (existingError) {
      existingError.remove();
    }

    // Create error message
    const errorDiv = document.createElement('div');
    errorDiv.className = 'cart-yardage-error';
    errorDiv.style.cssText = 'color: #d9534f; font-size: 0.875rem; margin-top: 0.5rem;';
    errorDiv.textContent = message;
    errorDiv.setAttribute('role', 'alert');

    const selector = cartItem.querySelector('.cart-yardage-selector');
    if (selector && selector.parentNode) {
      selector.parentNode.insertBefore(errorDiv, selector.nextSibling);
    }

    // Auto-remove after 5 seconds
    setTimeout(() => errorDiv.remove(), 5000);
  }

  /**
   * Set button loading state
   */
  function setButtonsLoading(cartItem, isLoading) {
    const minusBtn = cartItem.querySelector('.cart-yardage-minus');
    const plusBtn = cartItem.querySelector('.cart-yardage-plus');

    if (minusBtn && plusBtn) {
      minusBtn.disabled = isLoading;
      plusBtn.disabled = isLoading;

      if (isLoading) {
        minusBtn.style.opacity = '0.5';
        plusBtn.style.opacity = '0.5';
      } else {
        minusBtn.style.opacity = '1';
        plusBtn.style.opacity = '1';
      }
    }
  }

  /**
   * Update cart quantity via Shopify AJAX API
   */
  function updateCartQuantity(cartItem, lineIndex, newQuantity, oldQuantity) {
    // DEBUG: Log what we're about to send
    console.log('[Cart Yardage] UPDATE REQUEST:', {
      lineIndex: lineIndex,
      newQuantity: newQuantity,
      oldQuantity: oldQuantity
    });

    // Prevent duplicate requests for the same line
    if (pendingRequests.has(lineIndex)) {
      console.log('[Cart Yardage] Request already pending for line', lineIndex);
      return;
    }

    // Disable buttons during request
    setButtonsLoading(cartItem, true);
    pendingRequests.set(lineIndex, true);

    // Calculate yardage for line item property
    const yards = newQuantity / CONFIG.yardsToUnitsMultiplier;
    const yardText = yards === 1 ? 'yard' : 'yards';
    const yardageProperty = `${yards} ${yardText}`;

    // Update both quantity AND properties in a single call
    const requestBody = {
      line: lineIndex,
      quantity: newQuantity,
      properties: {
        'Yardage': yardageProperty
      }
    };
    console.log('[Cart Yardage] Sending to API:', JSON.stringify(requestBody));

    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody)
    })
    .then(response => {
      if (!response.ok) {
        throw new Error('Cart update failed');
      }
      return response.json();
    })
    .then(cart => {
      console.log('[Cart Yardage] Cart updated successfully');
      console.log('[Cart Yardage] API RESPONSE:', {
        item_count: cart.item_count,
        items: cart.items.map(item => ({
          title: item.title,
          quantity: item.quantity,
          final_line_price: item.final_line_price,
          properties: item.properties
        }))
      });

      // Update cart input to match API success
      updateCartInput(cartItem, newQuantity);

      // Update cart totals without reloading
      updateCartTotals(cart, lineIndex);
    })
    .catch(error => {
      console.error('[Cart Yardage] Error updating cart:', error);

      // CRITICAL: Revert both display AND cart input to old quantity
      updateYardageDisplay(cartItem, oldQuantity);
      updateCartInput(cartItem, oldQuantity);

      // Show error to user
      showError(cartItem, 'Failed to update quantity. Please try again.');
    })
    .finally(() => {
      // Re-enable buttons
      setButtonsLoading(cartItem, false);
      pendingRequests.delete(lineIndex);
    });
  }

  /**
   * Update cart totals in the UI
   */
  function updateCartTotals(cart, lineIndex) {
    // Update the specific line item total (FIX: use correct index)
    const cartItems = document.querySelectorAll('.cart-item');
    const targetItem = cartItems[lineIndex - 1];

    if (targetItem && cart.items[lineIndex - 1]) {
      const cartItem = cart.items[lineIndex - 1];
      // Select the SPAN specifically (not the struck-out price div)
      const lineTotal = targetItem.querySelector('.cart-item__total > span.theme-money');

      if (lineTotal) {
        // Total column ALWAYS shows the total line price (quantity × price)
        // For 1.5 yards at $4.98/yard, this shows $7.47 total
        lineTotal.textContent = formatMoney(cartItem.final_line_price);
        console.log('[Cart Yardage] Updated line total to:', formatMoney(cartItem.final_line_price));
      } else {
        console.warn('[Cart Yardage] Could not find line total element for line', lineIndex);
      }
    }

    // Update cart subtotal
    const subtotal = document.querySelector('.subtotal .theme-money');
    if (subtotal) {
      subtotal.textContent = formatMoney(cart.total_price);
    }
  }

  /**
   * Format money - uses theme's currency format if available
   */
  function formatMoney(cents) {
    // Try to use theme's money formatting if available
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, Shopify.money_format || '${{amount}}');
    }

    // Fallback to simple USD format
    return '$' + (cents / 100).toFixed(2);
  }

  /**
   * Handle +/- button clicks
   */
  function setupButtons(cartItem) {
    const minusBtn = cartItem.querySelector('.cart-yardage-minus');
    const plusBtn = cartItem.querySelector('.cart-yardage-plus');
    const yardageDisplay = cartItem.querySelector('.cart-yardage-display');
    const cartQuantityInput = cartItem.querySelector('[data-fsa-hidden-quantity-cart]');

    console.log('[Cart Yardage] Element check:', {
      minusBtn: !!minusBtn,
      plusBtn: !!plusBtn,
      yardageDisplay: !!yardageDisplay,
      cartQuantityInput: !!cartQuantityInput
    });

    if (!minusBtn || !plusBtn || !yardageDisplay || !cartQuantityInput) {
      console.log('[Cart Yardage] Missing required elements - skipping this item');
      return;
    }

    // Prevent duplicate initialization
    if (minusBtn.hasAttribute('data-yardage-initialized')) {
      console.log('[Cart Yardage] Already initialized, skipping');
      return;
    }
    minusBtn.setAttribute('data-yardage-initialized', 'true');
    plusBtn.setAttribute('data-yardage-initialized', 'true');

    // Get initial quantity from cart input (single source of truth)
    const initialQuantity = getCurrentQuantity(cartItem);
    console.log('[Cart Yardage] INIT - Cart quantity:', initialQuantity);

    // Update display to match actual cart quantity (fix any stale display)
    updateYardageDisplay(cartItem, initialQuantity);

    // Get line index from data attribute
    const lineIndex = parseInt(minusBtn.getAttribute('data-line'), 10);
    console.log('[Cart Yardage] INIT - Line index:', lineIndex);

    // Minus button
    minusBtn.addEventListener('click', (e) => {
      e.preventDefault();

      // Check if request already pending - if so, ignore click
      if (pendingRequests.has(lineIndex)) {
        console.log('[Cart Yardage] Click ignored - request already pending');
        return;
      }

      // Read current quantity from DOM (prevents race condition)
      const currentQuantity = getCurrentQuantity(cartItem);
      console.log('[Cart Yardage] MINUS CLICKED - Current:', currentQuantity);

      // Don't allow going below 0.5 yards (1 unit)
      const newQuantity = Math.max(1, currentQuantity - 1);

      if (newQuantity !== currentQuantity) {
        // Optimistically update display
        updateYardageDisplay(cartItem, newQuantity);

        // Update cart via AJAX
        updateCartQuantity(cartItem, lineIndex, newQuantity, currentQuantity);
      }
    });

    // Plus button
    plusBtn.addEventListener('click', (e) => {
      e.preventDefault();

      // Check if request already pending - if so, ignore click
      if (pendingRequests.has(lineIndex)) {
        console.log('[Cart Yardage] Click ignored - request already pending');
        return;
      }

      // Read current quantity from DOM (prevents race condition)
      const currentQuantity = getCurrentQuantity(cartItem);
      console.log('[Cart Yardage] PLUS CLICKED - Current:', currentQuantity);

      const newQuantity = currentQuantity + 1;

      // Optimistically update display
      updateYardageDisplay(cartItem, newQuantity);

      // Update cart via AJAX
      updateCartQuantity(cartItem, lineIndex, newQuantity, currentQuantity);
    });

    // Listen to cart input changes (catches manual edits)
    cartQuantityInput.addEventListener('change', () => {
      const newQuantity = parseInt(cartQuantityInput.value) || 0;
      const oldQuantity = parseInt(yardageDisplay.getAttribute('data-quantity')) || 0;

      if (newQuantity !== oldQuantity && newQuantity > 0) {
        console.log('[Cart Yardage] Quantity changed externally:', oldQuantity, '→', newQuantity);

        // Update display
        updateYardageDisplay(cartItem, newQuantity);

        // Update cart with new Yardage property
        updateCartQuantity(cartItem, lineIndex, newQuantity, oldQuantity);
      }
    });

    console.log(`[Cart Yardage] Buttons set up for line ${lineIndex}, initial quantity: ${initialQuantity} units`);
  }

  /**
   * Fix missing Yardage properties on cart items (for items added via quick add, etc.)
   */
  function fixMissingYardageProperties() {
    // Fetch current cart to check properties
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        console.log('[Cart Yardage] Checking for missing Yardage properties...');

        cart.items.forEach((item, index) => {
          const lineIndex = index + 1;
          const hasYardageProperty = item.properties && item.properties.Yardage;

          // Check if this is a fractional yardage product (has yardage selector in DOM)
          const cartItems = document.querySelectorAll('.cart-item');
          const cartItem = cartItems[index];
          const hasYardageSelector = cartItem && cartItem.querySelector('.cart-yardage-selector');

          if (hasYardageSelector && !hasYardageProperty) {
            console.log(`[Cart Yardage] Item "${item.title}" missing Yardage property - fixing...`);

            // Calculate yardage from current quantity
            const yards = item.quantity / CONFIG.yardsToUnitsMultiplier;
            const yardText = yards === 1 ? 'yard' : 'yards';
            const yardageProperty = `${yards} ${yardText}`;

            // Update via API to add the property
            fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                line: lineIndex,
                quantity: item.quantity,
                properties: {
                  'Yardage': yardageProperty
                }
              })
            })
            .then(response => response.json())
            .then(() => {
              console.log(`[Cart Yardage] Added Yardage property: ${yardageProperty}`);
            })
            .catch(error => {
              console.error('[Cart Yardage] Failed to add Yardage property:', error);
            });
          }
        });
      })
      .catch(error => {
        console.error('[Cart Yardage] Failed to fetch cart:', error);
      });
  }

  /**
   * Initialize yardage updater
   */
  function initYardageUpdater() {
    const cartItems = document.querySelectorAll('.cart-item');

    console.log(`[Cart Yardage] Found ${cartItems.length} cart items`);

    // Fix any missing Yardage properties first
    fixMissingYardageProperties();

    cartItems.forEach(cartItem => {
      // Only set up if yardage selector exists and not already initialized
      if (cartItem.querySelector('.cart-yardage-selector')) {
        setupButtons(cartItem);
      }
    });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initYardageUpdater);
  } else {
    initYardageUpdater();
  }

  // Re-initialize for Shopify section updates (theme editor)
  // setupButtons() now has duplicate protection via data-yardage-initialized attribute
  document.addEventListener('shopify:section:load', initYardageUpdater);

})();
